import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CreditCard, RefreshCw, Settings, Clock, CheckCircle, XCircle, BookOpen, Plus, Minus, Info, Calendar, Tag, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  PlanCard,
  AddSubjectModal,
  RenewSubjectModal,
  PaymentHistoryCard,
  getSubjectDisplayName,
  getDurationLabel,
  getStatusBadgeColor,
  getPlanBgGradient
} from "../subscription";

import ChildSubscriptionManager from "../ChildSubscriptionManager";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function SubscriptionTab({
  selectedChild,
  childrenList,
  selectChild,
  subscription,
  subStatus,
  paymentHistory,
  formatDate,
  getPlanDetails,
  setShowChangePlanModal,
  setSelectedPlan
}) {
  const navigate = useNavigate();
  const planDetails = getPlanDetails(subscription?.plan_type || "free_trial");
  
  // View toggle: 'new' for per-child model, 'legacy' for old model
  const [viewMode, setViewMode] = useState("new");
  
  // Subject management state
  const [subjectData, setSubjectData] = useState(null);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [modifyingSubject, setModifyingSubject] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  
  // Add subject modal state
  const [showAddSubjectModal, setShowAddSubjectModal] = useState(false);
  const [selectedSubjectToAdd, setSelectedSubjectToAdd] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState("monthly");
  const [addingSubject, setAddingSubject] = useState(false);
  
  // Renew subject modal state
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [selectedSubjectToRenew, setSelectedSubjectToRenew] = useState(null);
  const [renewDuration, setRenewDuration] = useState("monthly");
  const [renewingSubject, setRenewingSubject] = useState(false);
  
  const fetchSubjectData = async () => {
    if (!selectedChild?.id) return;
    setLoadingSubjects(true);
    try {
      const res = await fetch(`${API}/parent/child/${selectedChild.id}/subjects`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setSubjectData(data);
      }
    } catch (error) {
      console.error("Failed to fetch subjects:", error);
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Handle quick checkout for upgrade/renew
  const handleQuickCheckout = async (action) => {
    if (!selectedChild?.id) {
      toast.error("No child selected");
      return;
    }
    
    setCreatingOrder(true);
    try {
      // Determine plan based on action
      const currentPlan = subscription?.plan_type || "free_trial";
      let targetPlan = "starter";
      
      if (action === "upgrade") {
        const planOrder = { "free_trial": "starter", "starter": "plus", "plus": "pro" };
        targetPlan = planOrder[currentPlan] || "starter";
      } else {
        // Renew - keep same plan (or starter for trial users)
        targetPlan = currentPlan === "free_trial" ? "starter" : currentPlan;
      }
      
      const subjects = subscription?.subjects || ["math", "science", "english"];
      
      const res = await fetch(`${API}/payments/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          subscriber_id: selectedChild.id,
          plan_type: targetPlan,
          subjects: subjects,
          duration: "monthly"
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        // Redirect to checkout
        navigate(`/checkout?order_id=${data.order_id}`);
      } else {
        toast.error(data.detail || "Failed to create payment order");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to initiate checkout");
    } finally {
      setCreatingOrder(false);
    }
  };

  useEffect(() => {
    if (selectedChild?.id) {
      fetchSubjectData();
    }
  }, [selectedChild?.id]);
  
  const openAddSubjectModal = (subject) => {
    setSelectedSubjectToAdd(subject);
    setSelectedDuration("monthly");
    setShowAddSubjectModal(true);
  };
  
  const handleConfirmAddSubject = async () => {
    if (!selectedSubjectToAdd) return;
    
    setAddingSubject(true);
    try {
      const res = await fetch(`${API}/parent/child/${selectedChild.id}/subjects/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          child_id: selectedChild.id, 
          action: "add", 
          subject: selectedSubjectToAdd.subject,
          duration: selectedDuration
        })
      });
      
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        fetchSubjectData();
        setShowAddSubjectModal(false);
        setSelectedSubjectToAdd(null);
      } else {
        toast.error(data.detail || "Failed to add subject");
      }
    } catch (error) {
      toast.error("Failed to add subject");
    } finally {
      setAddingSubject(false);
    }
  };
  
  const handleRemoveSubject = async (subject) => {
    setModifyingSubject(subject);
    try {
      const res = await fetch(`${API}/parent/child/${selectedChild.id}/subjects/remove`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          child_id: selectedChild.id, 
          action: "remove", 
          subject: subject 
        })
      });
      
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        fetchSubjectData();
      } else {
        toast.error(data.detail || "Failed to remove subject");
      }
    } catch (error) {
      toast.error("Failed to remove subject");
    } finally {
      setModifyingSubject(null);
    }
  };
  
  const openRenewModal = (subjectDetail) => {
    setSelectedSubjectToRenew(subjectDetail);
    setRenewDuration(subjectDetail.duration || "monthly");
    setShowRenewModal(true);
  };
  
  const handleConfirmRenew = async () => {
    if (!selectedSubjectToRenew) return;
    
    setRenewingSubject(true);
    try {
      const res = await fetch(`${API}/parent/child/${selectedChild.id}/subjects/renew`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          child_id: selectedChild.id, 
          subject: selectedSubjectToRenew.subject,
          duration: renewDuration
        })
      });
      
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        fetchSubjectData();
        setShowRenewModal(false);
        setSelectedSubjectToRenew(null);
      } else {
        toast.error(data.detail || "Failed to renew subject");
      }
    } catch (error) {
      toast.error("Failed to renew subject");
    } finally {
      setRenewingSubject(false);
    }
  };
  
  const getSelectedPricing = () => {
    if (!selectedSubjectToAdd?.pricing || !selectedDuration) return null;
    return selectedSubjectToAdd.pricing[selectedDuration];
  };
  
  return (
    <div className="space-y-6">
      {/* New Per-Child Subscription Manager */}
      {childrenList && childrenList.length > 0 && (
        <ChildSubscriptionManager 
          childrenList={childrenList.map(child => ({
            ...child,
            student_id: child.student_id || child.id
          }))}
          selectedChild={selectedChild}
          onChildSelect={selectChild}
        />
      )}
      
      {/* Legacy Subscription Section - Only show if no children */}
      {(!childrenList || childrenList.length === 0) && (
        <>
      {/* Current Subscription */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            Current Subscription for {selectedChild?.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Current Plan Banner */}
          <div className={`p-6 rounded-xl mb-6 ${getPlanBgGradient(planDetails.color)} text-white`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-white/80 text-sm">Current Plan</p>
                <p className="text-2xl font-bold">{planDetails.name}</p>
                <p className="text-white/90 mt-1">
                  Daily Limit: <strong>{planDetails.dailyLimit}</strong>
                </p>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-3xl font-bold">
                  {planDetails.price === 0 ? "Free" : `₹${planDetails.price}`}
                  {planDetails.price > 0 && <span className="text-sm font-normal">/subject/month</span>}
                </p>
              </div>
            </div>
          </div>

          {/* Subscription Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Status</p>
              <p className={`font-bold text-lg ${subStatus.color === 'green' ? 'text-green-600' : subStatus.color === 'yellow' ? 'text-yellow-600' : 'text-red-600'}`}>
                {subStatus.text}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Start Date</p>
              <p className="font-bold">{formatDate(subscription?.start_date)}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">End Date</p>
              <p className="font-bold">{formatDate(subscription?.end_date)}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Subjects</p>
              <p className="font-bold">{subscription?.subjects?.length || 1}</p>
            </div>
          </div>

          {subscription?.status === 'grace_period' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800 font-medium">
                <Clock className="h-4 w-4 inline mr-2" />
                Your subscription is in grace period. Renew now to avoid interruption.
              </p>
            </div>
          )}

          {subscription?.plan_type === 'free_trial' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-800 font-medium">
                <Clock className="h-4 w-4 inline mr-2" />
                Your free trial gives you access to all Pro features. Upgrade before it ends to keep your progress!
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <Button 
              className="bg-blue-600 hover:bg-blue-700" 
              data-testid="renew-subscription-btn"
              onClick={() => handleQuickCheckout('renew')}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Renew Subscription
            </Button>
            {subscription?.plan_type !== 'pro' && (
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => handleQuickCheckout('upgrade')}
                data-testid="upgrade-plan-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Upgrade Plan
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => setShowChangePlanModal(true)}
              data-testid="change-plan-btn"
            >
              <Settings className="h-4 w-4 mr-2" />
              Change Plan
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Subject Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-emerald-600" />
            Manage Subjects for {selectedChild?.name}
          </CardTitle>
          <CardDescription>Add or remove subjects from your subscription. Choose monthly, 3-month, or 6-month cycles.</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingSubjects ? (
            <div className="text-center py-8 text-gray-500">Loading subjects...</div>
          ) : subjectData ? (
            <div className="space-y-6">
              {/* Pricing Info */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Base Price per Subject</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {subjectData.pricing_info?.base_price_per_subject_monthly === 0 
                        ? "Free Trial" 
                        : `₹${subjectData.pricing_info?.base_price_per_subject_monthly}/month`}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                      Monthly: No discount
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      <Tag className="h-3 w-3 mr-1" />
                      3 Months: 5% off
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                      <Tag className="h-3 w-3 mr-1" />
                      6 Months: 10% off
                    </span>
                  </div>
                </div>
              </div>

              {/* Current Subjects */}
              {subjectData.enrolled_subjects?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    Your Current Subjects ({subjectData.enrolled_subjects.length})
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {subjectData.enrolled_subjects.map((sub) => (
                      <div 
                        key={sub.subject} 
                        className={`p-4 rounded-lg border-2 ${getStatusBadgeColor(sub.status)}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="font-medium">{sub.display_name || getSubjectDisplayName(sub.subject)}</h5>
                            <div className="flex items-center gap-2 mt-1 text-xs">
                              <span className={`px-2 py-0.5 rounded-full ${getStatusBadgeColor(sub.status)}`}>
                                {sub.status}
                              </span>
                              <span className="text-gray-500">
                                {getDurationLabel(sub.duration)}
                              </span>
                            </div>
                            {sub.end_date && (
                              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Expires: {new Date(sub.end_date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {(sub.status === "pending_renewal" || sub.status === "expired") && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                onClick={() => openRenewModal(sub)}
                                data-testid={`renew-${sub.subject}-btn`}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRemoveSubject(sub.subject)}
                              disabled={modifyingSubject === sub.subject}
                              data-testid={`remove-${sub.subject}-btn`}
                            >
                              {modifyingSubject === sub.subject ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <Minus className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Available Subjects to Add */}
              {subjectData.available_subjects?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Plus className="h-4 w-4 text-blue-500" />
                    Add More Subjects
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {subjectData.available_subjects.map((sub) => (
                      <div 
                        key={sub.subject}
                        className="p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors flex items-center justify-between"
                      >
                        <span className="text-sm font-medium">{sub.display_name || getSubjectDisplayName(sub.subject)}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => openAddSubjectModal(sub)}
                          data-testid={`add-${sub.subject}-btn`}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Important Notes */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-medium text-amber-800 flex items-center gap-2 mb-2">
                  <Info className="h-4 w-4" />
                  Important Information
                </h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>Adding subjects takes effect immediately</li>
                  <li>Removing subjects takes effect from the next billing cycle</li>
                  <li>Subjects can only be removed 15+ days before renewal</li>
                  <li>3-month subscriptions get 5% discount</li>
                  <li>6-month subscriptions get 10% discount</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Unable to load subject information
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Subject Modal */}
      <AddSubjectModal
        open={showAddSubjectModal}
        onOpenChange={setShowAddSubjectModal}
        selectedSubject={selectedSubjectToAdd}
        selectedDuration={selectedDuration}
        setSelectedDuration={setSelectedDuration}
        getSelectedPricing={getSelectedPricing}
        onConfirm={handleConfirmAddSubject}
        loading={addingSubject}
      />

      {/* Renew Subject Modal */}
      <RenewSubjectModal
        open={showRenewModal}
        onOpenChange={setShowRenewModal}
        selectedSubject={selectedSubjectToRenew}
        renewDuration={renewDuration}
        setRenewDuration={setRenewDuration}
        onConfirm={handleConfirmRenew}
        loading={renewingSubject}
      />

      {/* Available Plans */}
      <Card>
        <CardHeader>
          <CardTitle>Available Plans</CardTitle>
          <CardDescription>Choose the best plan for {selectedChild?.name}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Starter Plan */}
            <PlanCard
              planId="starter"
              planName="Starter"
              price={499}
              color="emerald"
              currentPlan={subscription?.plan_type}
              features={[
                { text: "1 hour/day learning time", included: true },
                { text: "Basic progress tracking", included: true },
                { text: "Parent dashboard (view only)", included: true },
                { text: "No leaderboard access", included: false },
                { text: "No grace period", included: false }
              ]}
              onSelect={() => { setSelectedPlan("starter"); setShowChangePlanModal(true); }}
            />
            
            {/* Plus Plan - Recommended */}
            <PlanCard
              planId="plus"
              planName="Plus"
              price={699}
              color="blue"
              currentPlan={subscription?.plan_type}
              badge="RECOMMENDED"
              features={[
                { text: "2 hours/day learning time", included: true },
                { text: "Detailed progress analytics", included: true },
                { text: "Full parent dashboard", included: true },
                { text: "Leaderboard & rankings", included: true },
                { text: "Weekly progress reports", included: true },
                { text: "Practice tests & certificates", included: true },
                { text: "7-day grace period", included: true }
              ]}
              onSelect={() => { setSelectedPlan("plus"); setShowChangePlanModal(true); }}
            />
            
            {/* Pro Plan */}
            <PlanCard
              planId="pro"
              planName="Pro"
              price={899}
              color="purple"
              currentPlan={subscription?.plan_type}
              badge="BEST FOR EXAMS"
              features={[
                { text: "Unlimited learning time", included: true, bold: true },
                { text: "Advanced analytics & insights", included: true },
                { text: "Premium parent dashboard", included: true },
                { text: "Leaderboard + percentile ranks", included: true },
                { text: "Weekly + Quarterly reports", included: true },
                { text: "Practice tests & mock exams", included: true },
                { text: "AI-powered study plans", included: true },
                { text: "Progress certificates", included: true },
                { text: "7-day grace period", included: true }
              ]}
              onSelect={() => { setSelectedPlan("pro"); setShowChangePlanModal(true); }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Payment History */}
      <PaymentHistoryCard paymentHistory={paymentHistory} formatDate={formatDate} />
        </>
      )}
    </div>
  );
}
